// Pure form-payload helpers shared by TerritoryClientModal.jsx and
// its Jest suite. Kept in a plain .js module (no JSX, no React, no
// third-party imports) so the test file can import these helpers
// directly without pulling in react-router-dom / lucide-react /
// Mapbox at test time.
//
// Defect-3 forward fix (Feb 2026): the existing_client PATCH path
// uses ``buildDirtyPatch`` to send ONLY fields the user actually
// touched — diffing the cleaned current body against the cleaned
// prefill snapshot captured at modal open. Untouched blank seed
// values (null / []) compare equal to the snapshot are stripped
// from the PATCH; deliberate clears (null / false / []) DO differ
// and are transmitted so the row updates correctly.
//
// Feb-2026 hotfix (post-[19]): the parent widget seeds internal
// UI metadata onto the modal's ``initial`` prop under
// double-underscore keys (``__seededFromHome``, ``__hq_source``,
// ``__hq_home_id``) to route the CREATE via the CQC mark-home
// endpoint and identify the regulator origin. Those keys must
// NEVER be treated as persistent client fields — the backend does
// not accept them, does not echo them on PATCH/POST responses,
// and if allowed through they trigger a false-positive
// "Saved partially — these fields did not persist as submitted"
// warning on every fresh CQC-prospect save (both franchisee and
// HQ admin modes). ``cleanFormPayload`` therefore strips every
// key starting with ``__``, which means:
//   - ``body`` (CREATE/PATCH payload) never contains any ``__*``;
//   - ``snapshotClean`` never contains any ``__*``, so
//     ``buildDirtyPatch`` output never contains ``__*``;
//   - the echo-verification loop iterates over the cleaned
//     ``patchBody`` and therefore ignores ``__*`` too.
// The parent widget continues to read the ``__*`` keys off its
// own ``editingClient`` state to decide which mark-home route to
// take — those internal fields are never passed through the
// cleaner, so this hotfix does not disrupt any existing wiring.
//
// Dirty-field semantics are preserved verbatim: deliberate user
// changes to ``null``, ``false`` and ``[]`` still differ from the
// snapshot and are transmitted; untouched fields still compare
// equal and are omitted.

/**
 * Normalise a raw form state into the on-the-wire payload shape:
 *   - strip every ``__``-prefixed key (internal UI metadata);
 *   - trim strings and collapse empty strings to null;
 *   - drop fully-empty contact rows so we don't litter the DB;
 *   - normalise each surviving contact row to a stable shape so
 *     JSON.stringify diffs are order-and-shape stable across the
 *     dirty-field guard.
 * Applied to BOTH the current form (produces ``body``) AND the
 * snapshotted prefill (produces ``snapshotClean``) so the two are
 * diffed in the same shape and untouched blank seeds compare equal.
 */
export const cleanFormPayload = (f) => {
  const out = Object.fromEntries(
    Object.entries(f)
      // Internal UI metadata (parent-widget bookkeeping) never
      // travels to the backend — the server rejects unknown keys
      // via its allow-list and would drop them silently, causing
      // the echo-verification loop below to flag a false
      // "Saved partially" mismatch on every fresh CQC-prospect save.
      .filter(([k]) => !k.startsWith("__"))
      .map(([k, v]) => {
        if (k === "contacts") return [k, v];
        return [k, typeof v === "string" ? (v.trim() || null) : v];
      })
  );
  out.contacts = (f.contacts || []).filter((c) =>
    (c.name || c.role || c.phone || c.email || c.notes || "").trim()
  ).map((c) => ({
    name: (c.name || "").trim() || null,
    role: (c.role || "").trim() || null,
    phone: (c.phone || "").trim() || null,
    email: (c.email || "").trim() || null,
    notes: (c.notes || "").trim() || null,
    include_for_marketing: c.include_for_marketing !== false,
  }));
  return out;
};

/**
 * Build the dirty-field PATCH body for the existing_client path.
 * For every key in the cleaned current body, include it iff its
 * value differs from the cleaned prefill snapshot. This means:
 *   - untouched blank seed fields (null/[] on both sides) are
 *     OMITTED — so they can never overwrite the existing row's
 *     populated CRM state;
 *   - deliberate user changes are INCLUDED, including changes to
 *     ``null`` (clearing a populated field), ``false`` (toggling
 *     a boolean off) and ``[]`` (removing the final contact).
 * Deep-compare via JSON.stringify — sufficient because
 * ``cleanFormPayload`` produces stable-shape values for both sides.
 */
export const buildDirtyPatch = (bodyClean, snapshotClean) =>
  Object.fromEntries(
    Object.entries(bodyClean).filter(([k, v]) =>
      JSON.stringify(v) !== JSON.stringify(snapshotClean?.[k])
    )
  );
